import { RiskboxComponent } from '../../egp/riskbox/riskbox.component';
import { LoadingOverlayComponent } from '../../shared/components/loading-overlay/loading-overlay.component';

import { EgpResponse, EgpService } from '../../egp/egp.service';
import { LanguageService } from 'src/app/shared/services/language.service';

import { TitlePipe } from '../../egp/pipes/title.pipe';
import { WDayPipe } from '../../egp/pipes/w-day.pipe';
import { DatexPipe } from '../../shared/datex.pipe';

import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { Moment } from 'moment';
import moment from 'moment';
import { isNil } from 'src/utils/utils';

export const MODULES = [CommonModule, TranslateModule];
export const COMPONENTS = [RiskboxComponent, FaIconComponent];
export const PIPES = [DatexPipe];

@Component({
  selector: 'app-dashboard-egp',
  templateUrl: './dashboard-egp.component.html',
  styleUrls: ['./dashboard-egp.component.scss', '../dashboard.component.scss'],
  imports: [
    ...MODULES,
    ...COMPONENTS,
    ...PIPES,
    LoadingOverlayComponent,
    TitlePipe,
    WDayPipe,
  ],
})
export class DashboardEgpComponent implements OnInit {
  private injector = inject(Injector);
  private egpService = inject(EgpService);
  public languageService = inject(LanguageService);

  egpConfig = input<any>();
  refresh = input<number>();
  // Blendet das intern gerenderte Zeitstempel-Badge aus, wenn ein
  // umgebendes Widget den Zeitstempel selbst (z.B. im eigenen Header) anzeigt.
  showUpdatedChip = input<boolean>(true);
  // Wenn gesetzt, wird diese gespeicherte EGP-Antwort angezeigt statt live
  // abgerufen (z.B. beim erneuten Öffnen eines abgeschlossenen Einsatzes).
  snapshotData = input<any>(null);
  dtgChange = output<string | null>();
  // Rohantwort des aktuell angezeigten EGP-Standes (egal ob live geladen
  // oder Snapshot) – ermöglicht dem Elternteil, sie z.B. beim Speichern
  // eines neuen Einsatzprotokolls als Snapshot zu übernehmen.
  egpResponseChange = output<any>();

  previousRefresh = signal(null);
  egpData = signal<any>(undefined);
  isLoading = signal(true);
  latestDtg: Moment;

  constructor() {
    this.refreshEgpData();
  }

  ngOnInit(): void {
    const snapshotData = this.snapshotData();
    if (snapshotData) {
      this.egpData.set(snapshotData);
      this.isLoading.set(false);
      this.dtgChange.emit(snapshotData?.textObject?.prescribeAt ?? null);
      this.egpResponseChange.emit(snapshotData);
      return;
    }

    const egpConfig = this.egpConfig();
    if (egpConfig) {
      this.fetchEgpData(egpConfig['region']);
    }
  }

  fetchEgpData(region): Promise<void> {
    return new Promise((resolve, reject) => {
      this.egpService
        .getEgp(region)
        .then((currentAndPreviousEgp: Array<EgpResponse>) => {
          const egpData = currentAndPreviousEgp[0];
          const dtg = moment(egpData['textObject']['prescribeAt']);
          if (!this.latestDtg || this.latestDtg.isBefore(dtg)) {
            this.isLoading.set(true);
            this.latestDtg = dtg;

            const isOutdated = this.isEgpOutdated(dtg);
            const resolvedEgpData = isOutdated ? null : { ...egpData };
            this.egpData.set(resolvedEgpData);
            this.dtgChange.emit(
              isOutdated ? null : egpData['textObject']['prescribeAt']
            );
            this.egpResponseChange.emit(resolvedEgpData);
            this.isLoading.set(false);
          }
          resolve();
        })
        .catch(err => {
          console.log('Error: No egp for ', region);
          this.isLoading.set(false);
          reject();
        });
    });
  }

  isEgpOutdated(dtg): boolean {
    const today = moment();
    const daysWhenEgpIsTooOld = 30;

    return today.diff(dtg, 'days') >= daysWhenEgpIsTooOld;
  }

  private refreshEgpData() {
    effect(
      () => {
        const refresh = this.refresh();
        untracked(() => {
          const previousRefresh = this.previousRefresh();
          if (!isNil(previousRefresh) && refresh > previousRefresh) {
            const egpConfig = this.egpConfig();
            if (egpConfig && this.egpData()) {
              this.fetchEgpData(egpConfig['region']);
            }
          }
          this.previousRefresh.set(refresh);
        });
      },
      { injector: this.injector }
    );
  }
}
